import React from 'react';
import './ThemePhoto.css';

/** Skutečné šířky obou variant fotky v pixelech (náhled „-t“ a velká verze). */
export interface PhotoWidths {
  thumb: number;
  full: number;
}

// Tak velké jsou fotky z galerií (scripts/build-photos.mjs: THUMB a FULL).
// Užší originál se nezvětšuje – u takové fotky je potřeba zadat skutečnou
// šířku přes `widths` (zjistí se např. příkazem `file <fotka>.webp`).
const DEFAULT_WIDTHS: PhotoWidths = { thumb: 800, full: 1600 };

interface ThemePhotoProps {
  /** Cesta k fotce bez přípony, např. "/photos/galerie/rodinne/01". */
  base: string;
  alt: string;
  /** Náhledová (menší) varianta – do dlaždic a pásku pod galerií. */
  thumb?: boolean;
  /** "cover" fotku ořízne do rámečku, "contain" ukáže celou. */
  fit?: 'cover' | 'contain';
  className?: string;
  /** Fotky nad ohybem se mají načíst hned, ostatní až když na ně dojde. */
  eager?: boolean;
  /**
   * HTML atribut sizes – jak široká fotka na stránce bude, např.
   * "(max-width: 768px) 100vw, 50vw". Se zadaným sizes si prohlížeč sám
   * vybere náhled, nebo velkou verzi (srcset), viz komentář níž.
   */
  sizes?: string;
  /** Šířky variant pro srcset – jen když se liší od 800 / 1600 px. */
  widths?: PhotoWidths;
}

/**
 * Fotka, která je ve světlém režimu barevná a v tmavém černobílá.
 *
 * Obě verze leží v DOM přes sebe a přepínají se průhledností, takže přepnutí
 * režimu je plynulé a nečeká se na stažení druhé varianty. Černobílá verze
 * není odbarvená filtrem – je to autorský černobílý export fotografky.
 *
 * Se zadaným `sizes` dostanou obě <img> srcset s náhledem i velkou verzí:
 * jedna pevná varianta je vždycky někde špatně – náhled (800 px) je na
 * velkém nebo hustém displeji měkký, velká verze se zbytečně stahuje do
 * mobilu. Prohlížeč podle sizes a hustoty displeje vezme tu nejmenší, která
 * stačí. `src` zůstává jako záloha pro prohlížeče bez srcset.
 *
 * Stahují se vždy obě <img> (černobílá je jen průhledná), takže velká verze
 * stojí dvakrát. Proto se na telefonu v sizes schválně uvádí menší šířka, než
 * fotka má (266 px → náhled stačí i na hustotě 3×), a naopak u fotek přes půl
 * obrazovky na počítači pevná velká šířka – viz Portfolio, ChristmasBooking.
 */
const ThemePhoto: React.FC<ThemePhotoProps> = ({
  base,
  alt,
  thumb = false,
  fit = 'cover',
  className = '',
  eager = false,
  sizes,
  widths = DEFAULT_WIDTHS,
}) => {
  const suffix = thumb ? '-t' : '';
  const loading = eager ? undefined : 'lazy';

  // „-bw“ pro černobílou verzi; srcset jen se sizes – bez něj by prohlížeč
  // počítal s fotkou přes celou šířku okna a na počítači stahoval zbytečně
  // velkou verzi i do malé dlaždice
  const srcSet = (variant: '' | '-bw') =>
    sizes
      ? `${base}${variant}-t.webp ${widths.thumb}w, ${base}${variant}.webp ${widths.full}w`
      : undefined;

  // Pořadí atributů je schválně: loading a sizes před srcset a src – React
  // je nastavuje popořadě a prohlížeč může začít stahovat hned při src
  // (starší Firefox kvůli tomu ignoroval lazy), ať už zná všechno.
  return (
    <span className={`theme-photo theme-photo--${fit} ${className}`.trim()}>
      <img
        loading={loading}
        decoding="async"
        sizes={sizes}
        srcSet={srcSet('')}
        src={`${base}${suffix}.webp`}
        alt={alt}
        className="theme-photo__img theme-photo__img--color"
        draggable={false}
      />
      <img
        loading={loading}
        decoding="async"
        sizes={sizes}
        srcSet={srcSet('-bw')}
        src={`${base}-bw${suffix}.webp`}
        alt=""
        aria-hidden="true"
        className="theme-photo__img theme-photo__img--bw"
        draggable={false}
      />
    </span>
  );
};

export default ThemePhoto;
